import { isImageMime } from '@footsteps/shared';
import type { WozCaseAttachment, AttachmentSource, AttachmentType } from '../../woz-case/woz-case.types';
import { SupabaseApiService } from '../supabase-api/supabase-api.service';

/**
 * Supabase-backed attachment service.
 * Uploads files to the Edge Function which stores them in Supabase Storage
 * and maintains metadata in the `case_attachments` table.
 */
export class WozCaseAttachmentService {
  private readonly api = SupabaseApiService.getInstance();

  async upload(
    file: File,
    caseId: string,
    source: AttachmentSource,
    options?: {
      caseComplaintId?: string;
      caseComplaintQuestionId?: string;
      description?: string;
    }
  ): Promise<WozCaseAttachment> {
    const type: AttachmentType = isImageMime(file.type) ? 'image' : 'file';

    const formData = new FormData();
    formData.append('file', file);
    formData.append('caseId', caseId);
    formData.append('source', source);
    formData.append('type', type);

    if (options?.caseComplaintId) formData.append('caseComplaintId', options.caseComplaintId);
    if (options?.caseComplaintQuestionId) formData.append('caseComplaintQuestionId', options.caseComplaintQuestionId);
    if (options?.description) formData.append('description', options.description);

    const response = await this.api.uploadAttachment(formData);
    return response;
  }

  async list(caseId: string): Promise<WozCaseAttachment[]> {
    const data = await this.api.listAttachments(caseId);
    return data;
  }

  async getSignedUrl(attachmentId: string): Promise<string> {
    return this.api.getAttachmentSignedUrl(attachmentId);
  }

  async updateDescription(attachmentId: string, description: string | null): Promise<WozCaseAttachment> {
    const response = await this.api.updateAttachmentDescription(attachmentId, description);
    return response;
  }

  async delete(attachmentId: string): Promise<void> {
    await this.api.deleteAttachment(attachmentId);
  }
}
